/**
 * メタタグ設定の中央集権管理
 * 各ページのメタ情報とSEO設定を統一的に管理します
 */

import { getPetPrefectureSlug, type PetPrefecture } from "@nekometry/core";
import type { AdoptionPlaceType } from "@/domains/adoption/AdoptionPlace";
import type { ArticleSummary } from "@/domains/articles/Article";
import type { CatLifeStage } from "@/domains/cats/CatAge";
import type { FoodSafetyEntry } from "@/domains/food-safety/FoodSafetyEntry";
import {
  NEKOSEI_GAME_FULL_NAME,
  NEKOSEI_PET_CAT_COUNT_LABEL,
} from "@/domains/nekosei/NekoseiGameName";
import BodyConditionOgpImage from "@/assets/images/tools/ogp-body-condition.jpg";
import FirstCatGameOgpImage from "@/assets/images/tools/ogp-first-cat.jpg";
import FoodsSafetyOgpImage from "@/assets/images/tools/ogp-foods-safety.jpg";
import NekoseiGameOgpImage from "@/assets/images/tools/ogp-nekosei.jpg";
import WelcomePlanOgpImage from "@/assets/images/tools/ogp-welcome-plan.jpg";
import { ageConcernsOf } from "@/constants/AgeConcerns";
import { LIFE_STAGE_OGP_IMAGES } from "@/constants/LifeStages";
import { SITE_BASE_URL, COMMON_BREADCRUMB_ITEMS } from "@/constants/Site";

export interface MetaConfig {
  title: string;
  description: string;
  url: string;
  type?: "website" | "article";
  structuredDataType?: "calorie" | "transition" | "website" | "bodyCondition";
  ogImage?: string;
}

/**
 * ページ別メタタグ設定
 */
export const META_CONFIGS = {
  home: {
    title: "Nekometry | 猫のごはん量・カロリー計算ツール",
    description:
      "Nekometryは、愛猫の健康管理をサポートするWebサービスです。市販のキャットフードのカロリー計算やフード切り替えなどのツールを提供しています。",
    url: COMMON_BREADCRUMB_ITEMS.home.url,
    type: "website" as const,
    structuredDataType: "website" as const,
  },

  calorie: {
    title: "猫のごはん量計算 | 必要カロリーから1日のg数がわかる | Nekometry",
    description:
      "愛猫に必要なカロリー(RER、DER)とキャットフードの量を計算できるツールです。市販の餌のカロリー表記を元に何グラム与えると良いかを計算します。",
    url: COMMON_BREADCRUMB_ITEMS.calorie.url,
    type: "website" as const,
    structuredDataType: "calorie" as const,
  },

  foods: {
    title: "キャットフード一覧 | カロリーから給餌量を計算 | Nekometry",
    description:
      "猫のカロリー計算に対応しているフードの一覧を表示します。各フードのカロリーや栄養成分を確認できます。",
    url: COMMON_BREADCRUMB_ITEMS.foods.url,
    type: "website" as const,
  },

  transition: {
    title: "猫のフード切り替えプラン作成 | 混合比と量を自動計算 | Nekometry",
    description:
      "猫のフードを安全に切り替えるためのプランナーです。必要なカロリーを維持しながら、徐々に新しいフードに変更する計画を立てられます。",
    url: COMMON_BREADCRUMB_ITEMS.transition.url,
    type: "website" as const,
    structuredDataType: "transition" as const,
  },

  videos: {
    title: "猫のおすすめ動画 | 癒やしと学びのチャンネル紹介 | Nekometry",
    description:
      "癒やしと学びのおすすめ猫動画チャンネルから最新の動画をお届けします。猫好きの方に向けた厳選動画を紹介しています。",
    url: COMMON_BREADCRUMB_ITEMS.videos.url,
    type: "website" as const,
  },

  pets: {
    title: "ペットショップの子猫一覧 | 猫種・価格で探す(非公式) | Nekometry",
    description:
      "提携ペットショップの猫一覧を見やすくまとめた非公式ページです。猫種・性別・価格で絞り込みながら、気になる子を確認できます。",
    url: COMMON_BREADCRUMB_ITEMS.pets.url,
    type: "website" as const,
  },

  age: {
    title: "猫の年齢を人間に換算 | うちの猫は人間なら何歳？ | Nekometry",
    description:
      "猫の年齢（歳・月齢）から人間でいうと何歳かを換算できる無料ツールです。子猫期・成猫期・シニア期などのライフステージと年齢早見表も確認できます。",
    url: COMMON_BREADCRUMB_ITEMS.age.url,
    type: "website" as const,
  },

  mydata: {
    title: "マイデータ | 保存されている入力内容の確認・削除 | Nekometry",
    description:
      "Nekometryの各ツールで入力した体重や年齢などの保存内容を確認し、不要になったデータをこのブラウザから削除できます。データはブラウザ内にのみ保存され、サーバーには送信されません。",
    url: COMMON_BREADCRUMB_ITEMS.mydata.url,
    type: "website" as const,
  },

  terms: {
    title: "利用規約 | Nekometry",
    description:
      "Nekometryの利用規約です。提供するサービスの内容、計算ツールの結果や記事の位置づけ、禁止事項、外部サイトや広告、免責事項について定めています。",
    url: COMMON_BREADCRUMB_ITEMS.terms.url,
    type: "website" as const,
  },
  privacy: {
    title: "プライバシーポリシー | Nekometry",
    description:
      "Nekometryで取得する情報と利用目的、ブラウザ内への保存、利用している外部サービス、お問い合わせ方法について説明します。",
    url: COMMON_BREADCRUMB_ITEMS.privacy.url,
    type: "website" as const,
  },

  cost: {
    title: "猫を迎える費用を知る | 初回・毎月・毎年の費用を確認 | Nekometry",
    description: "猫を迎えるときの費用目安を、条件を選んで確認できます。",
    url: COMMON_BREADCRUMB_ITEMS.cost.url,
    type: "website" as const,
    ogImage: WelcomePlanOgpImage,
  },

  articles: {
    title: "猫の暮らしに役立つ記事一覧 | Nekometry",
    description:
      "猫を迎える費用や1日に必要なカロリー、おやつの量の目安など、猫との生活に役立つ記事をまとめています。",
    url: COMMON_BREADCRUMB_ITEMS.articles.url,
    type: "website" as const,
  },

  bodyCondition: {
    title:
      "猫のBCS（体型）チェック | 9段階の早見表と理想体重の目安 | Nekometry",
    description:
      "猫のボディコンディションスコア（BCS）を、9段階のシルエットと見比べてチェックできます。BCS早見表と、今の体重から理想体重の目安を求める方法も紹介。WSAVAとAAHAのガイドラインをもとに、ごはん量計算にもつなげられます。",
    url: COMMON_BREADCRUMB_ITEMS.bodyCondition.url,
    type: "website" as const,
    structuredDataType: "bodyCondition" as const,
    ogImage: BodyConditionOgpImage,
  },

  foodsSafety: {
    title: "猫が食べても大丈夫？ | 与えてはいけない食べ物を調べる | Nekometry",
    description:
      "タマネギやチョコレートなど、猫に与えてはいけない食べ物と注意が必要な食べ物を、環境省のガイドラインとFDA（米国食品医薬品局）の資料をもとに調べられます。少量でも命に関わるユリも掲載しています。",
    url: COMMON_BREADCRUMB_ITEMS.foodsSafety.url,
    type: "website" as const,
    ogImage: FoodsSafetyOgpImage,
  },

  events: {
    title: "猫のイベントを探す | 猫フェス・即売会・展示の開催予定 | Nekometry",
    description:
      "にゃんだらけやねこ休み展など毎年開かれる猫のイベントと、展示・マルシェの開催予定を地方ごとにまとめています。次回が未発表の定番イベントは、例年の開催時期を確認できます。",
    url: COMMON_BREADCRUMB_ITEMS.events.url,
    type: "website" as const,
  },

  firstCatGame: {
    title:
      "はじめて猫を飼う暮らしを体験する | ドット絵のお世話ゲーム | Nekometry",
    description:
      "はじめて猫を飼う方に向けたドット絵のお世話ゲームです。ごはんやトイレ、動物病院への通院など、猫を迎えてから6か月のあいだに起きる出来事とかかるお金を、約10分で体験できます。",
    url: COMMON_BREADCRUMB_ITEMS.firstCatGame.url,
    type: "website" as const,
    ogImage: FirstCatGameOgpImage,
  },
  nekoseiGame: {
    title: `${NEKOSEI_GAME_FULL_NAME} 猫として一生を生きるゲーム | Nekometry`,
    description: `日本の飼い猫${NEKOSEI_PET_CAT_COUNT_LABEL}のうちの1匹として生まれ、一生を最後まで生きるゲームです。生まれる場所も、人間の呼び方も選べません。コップを落とすか、病院から逃げるかは、猫のあなたが決めます。`,
    url: COMMON_BREADCRUMB_ITEMS.nekoseiGame.url,
    type: "website" as const,
    ogImage: NekoseiGameOgpImage,
  },
} as const;

const DESCRIPTION_MAX_LENGTH = 120;

const truncateDescription = (text: string): string =>
  text.length <= DESCRIPTION_MAX_LENGTH
    ? text
    : `${text.slice(0, DESCRIPTION_MAX_LENGTH - 1)}…`;

/**
 * 動的ページ用のメタタグ生成関数
 */
/** 都道府県ページの description に名前を出す市区町村の数 */
const ADOPTION_DESCRIPTION_CITY_LIMIT = 3;

/** 検索結果で読まれる title・description 向けの、施設の種類の短い呼び方 */
const ADOPTION_META_TYPE_LABELS: Record<AdoptionPlaceType, string> = {
  shelter: "保護団体",
  cafe: "譲渡型保護猫カフェ",
  municipal: "動物愛護センター",
};

export const createDynamicMetaConfig = {
  foodDetailDescription: (foodLabel: string): string =>
    `${foodLabel}のカロリーと栄養成分をもとに、猫の状態に合わせた1日の給餌量（グラム）を計算できます。`,

  /**
   * 参考文献記事用のメタ設定を生成
   */
  referenceArticle: (
    title: string,
    description: string,
    url: string,
  ): MetaConfig => ({
    title: `${title} | 猫のごはんの参考文献 | Nekometry`,
    description,
    url,
    type: "article",
  }),

  /**
   * 年齢換算ガイドページ（/age/{年齢}/）用のメタ設定を生成
   */
  ageYearGuide: (
    years: number,
    humanAge: number,
    lifeStage: CatLifeStage,
  ): MetaConfig => {
    const [firstQuestion, secondQuestion] = ageConcernsOf(years).map(
      (concern) => concern.question,
    );
    return {
      title: `猫の${years}歳は人間でいうと約${humanAge}歳 | ${firstQuestion} | Nekometry`,
      description: `猫の${years}歳は人間でいうと約${humanAge}歳、${lifeStage.label}にあたります。${firstQuestion}${secondQuestion}${years}歳の猫で気にかけたい変化と、食事・健康診断の目安を獣医学のガイドラインや環境省の資料をもとにまとめました。`,
      url: `${SITE_BASE_URL}/age/${years}/`,
      type: "article",
      ogImage: LIFE_STAGE_OGP_IMAGES[lifeStage.key],
    };
  },

  /**
   * 都道府県ごとの保護猫案内ページ（/adoption/{都道府県}/）用のメタ設定を生成
   */
  adoptionPrefecture: (
    prefecture: PetPrefecture,
    placeCount: number,
    citiesByPlaceCount: readonly string[],
    placeTypes: readonly AdoptionPlaceType[],
  ): MetaConfig => {
    const typeLabels = placeTypes.map(
      (type) => ADOPTION_META_TYPE_LABELS[type],
    );
    const cityPhrase =
      citiesByPlaceCount.length > ADOPTION_DESCRIPTION_CITY_LIMIT
        ? `${citiesByPlaceCount.slice(0, ADOPTION_DESCRIPTION_CITY_LIMIT).join("・")}など${String(citiesByPlaceCount.length)}市区町村`
        : citiesByPlaceCount.join("・");

    return {
      title: `${prefecture}で保護猫の里親になれる場所${String(placeCount)}件 | ${typeLabels.join("・")} | Nekometry`,
      description: `${prefecture}で保護猫の里親になりたい方へ。${cityPhrase}の${typeLabels.join("、")}${String(placeCount)}件を地図と一覧で紹介します。譲渡条件や募集中の猫は各施設の公式サイトへ案内します。`,
      url: `${SITE_BASE_URL}/adoption/${getPetPrefectureSlug(prefecture)}/`,
      type: "website",
    };
  },

  /**
   * 年ごとの猫のイベント一覧ページ（/events/{年}/）用のメタ設定を生成
   */
  eventsYear: (year: number, eventCount: number): MetaConfig => ({
    title: `${String(year)}年の猫のイベント一覧 | 猫フェス・即売会・展示${String(eventCount)}件 | Nekometry`,
    description: `${String(year)}年の猫のイベント${String(eventCount)}件を月ごとにまとめています。猫フェスや即売会、展示の日程と会場を、主催者の公式情報へのリンクとあわせて確認できます。`,
    url: `${COMMON_BREADCRUMB_ITEMS.events.url}${String(year)}/`,
    type: "website",
  }),

  /**
   * 保護猫案内の集約ページ（/adoption/）用のメタ設定を生成
   */
  adoption: (prefectureCount: number, placeCount: number): MetaConfig => ({
    title:
      "保護猫の里親になれる場所を都道府県から探す | 保護団体・譲渡型保護猫カフェ・動物愛護センター | Nekometry",
    description: `保護猫の里親になりたい方へ。${String(prefectureCount)}都道府県の保護団体、譲渡型保護猫カフェ、動物愛護センター${String(placeCount)}件を都道府県別に紹介します。各施設の公式サイトや全国の里親募集サービスへ案内します。`,
    url: COMMON_BREADCRUMB_ITEMS.adoption.url,
    type: "website",
  }),

  /**
   * フード個別ページ用のメタ設定を生成
   */
  foodDetail: (foodName?: string, url?: string): MetaConfig => ({
    title: foodName
      ? `${foodName}の給餌量計算 | 1日に何gあげる？ | Nekometry`
      : "キャットフードの給餌量計算 | 1日に何gあげる？ | Nekometry",
    description: foodName
      ? createDynamicMetaConfig.foodDetailDescription(foodName)
      : createDynamicMetaConfig.foodDetailDescription("対象フード"),
    url: url || `${SITE_BASE_URL}/calorie/food/`,
    type: "website",
  }),

  /**
   * 猫が食べても大丈夫か調べる詳細ページ（/foods-safety/{slug}/）用のメタ設定を生成
   */
  foodSafetyDetail: (entry: FoodSafetyEntry): MetaConfig => ({
    title: `猫は${entry.name}を口にしても大丈夫？ | Nekometry`,
    description: truncateDescription(entry.cause),
    url: `${COMMON_BREADCRUMB_ITEMS.foodsSafety.url}${entry.slug}/`,
    type: "article",
    ogImage: FoodsSafetyOgpImage,
  }),

  /**
   * 記事詳細ページ（/articles/{slug}/）用のメタ設定を生成
   */
  article: (article: ArticleSummary): MetaConfig => ({
    title: `${article.title} | Nekometry`,
    description: article.description,
    url: `${SITE_BASE_URL}/articles/${article.slug}/`,
    type: "article",
    ogImage: article.ogImageUrl,
  }),
};

/**
 * ルート重複チェック関数
 * 子ルートでの重複レンダリングを防止します
 */
export const shouldSkipMetaRendering = {
  /**
   * カロリーページの子ルートチェック
   */
  calorieChildRoutes: (pathname: string): boolean => {
    return (
      pathname.includes("/calorie/food/") ||
      pathname.includes("/calorie/foods") ||
      pathname.includes("/calorie/reference") ||
      pathname.includes("/calorie/transition")
    );
  },

  /**
   * 年齢換算ガイドページ（/age/{年齢}/）の動的ルートチェック
   */
  ageYearsRoute: (routeParams?: Record<string, string>): boolean => {
    return Boolean(routeParams?.years);
  },

  /**
   * 都道府県ごとの保護猫案内ページ（/adoption/{都道府県}/）の動的ルートチェック
   */
  adoptionPrefectureRoute: (routeParams?: Record<string, string>): boolean => {
    return Boolean(routeParams?.prefecture);
  },

  /**
   * 年ごとの猫のイベント一覧ページの動的ルートチェック
   */
  eventsYearRoute: (routeParams?: Record<string, string>): boolean => {
    return Boolean(routeParams?.year);
  },

  /**
   * 記事詳細ページの動的ルートチェック
   */
  articleSlugRoute: (routeParams?: Record<string, string>): boolean => {
    return Boolean(routeParams?.slug);
  },

  /**
   * 猫が食べても大丈夫か調べる詳細ページの動的ルートチェック
   */
  foodsSafetySlugRoute: (routeParams?: Record<string, string>): boolean => {
    return Boolean(routeParams?.slug);
  },
};
